"use client";

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { BROWSE_BY_AREA_ID, browseByAreaPanelId } from "./browse-by-area-ids";

/**
 * The F4 Coast / Mountains segmented control for the hub's Explore by area block, built as
 * WAI-ARIA tabs. Both panels are in the server HTML (crawlers follow every area link); the
 * inactive one is hidden. A link to `#browse-by-area-{tab}` opens that tab. Keyboard: one tab
 * stop (roving tabindex), with Left, Right, Home and End moving between tabs and selecting them.
 */
export type BrowseByAreaTab = Readonly<{
  id: string;
  label: string;
  sublabel: string;
  /** Screen reader announcement when the tab is chosen, for example "Showing coast areas, ranked for surf". */
  announce: string;
  panel: ReactNode;
}>;

export function BrowseByAreaTabs({ tabs, heading }: { tabs: readonly BrowseByAreaTab[]; heading?: ReactNode }) {
  const [active, setActive] = useState(tabs[0]?.id ?? "");
  const [announced, setAnnounced] = useState("");
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function choose(id: string) {
    setActive(id);
    setAnnounced(tabs.find((tab) => tab.id === id)?.announce ?? "");
  }

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = tabs.length - 1;
    const next =
      event.key === "ArrowRight" ? (index === last ? 0 : index + 1)
        : event.key === "ArrowLeft" ? (index === 0 ? last : index - 1)
          : event.key === "Home" ? 0
            : event.key === "End" ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    choose(tab.id);
    tabRefs.current[next]?.focus();
  }

  useEffect(() => {
    const fromHash = () => {
      const hash = window.location.hash.slice(1);
      const tab = tabs.find((candidate) => browseByAreaPanelId(candidate.id) === hash);
      if (!tab) return;
      setActive(tab.id);
      queueMicrotask(() => {
        document.getElementById(BROWSE_BY_AREA_ID)?.scrollIntoView({ block: "start" });
      });
    };
    queueMicrotask(fromHash);
    window.addEventListener("hashchange", fromHash);
    return () => window.removeEventListener("hashchange", fromHash);
  }, [tabs]);

  return (
    <div>
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
        {heading}
        <div
          className="flex w-full rounded-[18px] border border-border bg-white p-1 md:w-[380px] md:shrink-0 md:rounded-2xl"
          role="tablist"
          aria-label="Area type"
        >
          {tabs.map((tab, index) => {
            const selected = tab.id === active;
            return (
              <button
                key={tab.id}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                id={`${browseByAreaPanelId(tab.id)}-tab`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={browseByAreaPanelId(tab.id)}
                tabIndex={selected ? 0 : -1}
                onClick={() => choose(tab.id)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={`flex min-h-[54px] flex-1 basis-0 cursor-pointer flex-col items-center justify-center gap-px rounded-[14px] border-0 px-3 py-1.5 font-sans leading-[1.2] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark motion-reduce:transition-none md:min-h-[52px] md:rounded-[12px] md:px-3.5 md:py-1 ${
                  selected ? "bg-navy text-white" : "bg-transparent text-navy hover:bg-warm-bg"
                }`}
              >
                <span className="text-[15px] font-bold">{tab.label}</span>
                <span className={`text-[12px] font-medium ${selected ? "text-[#C9D3E0]" : "text-[#566072]"}`}>{tab.sublabel}</span>
              </button>
            );
          })}
        </div>
      </div>
      <p className="sr-only" aria-live="polite">{announced}</p>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          id={browseByAreaPanelId(tab.id)}
          role="tabpanel"
          aria-labelledby={`${browseByAreaPanelId(tab.id)}-tab`}
          hidden={tab.id !== active}
          className="scroll-mt-24"
        >
          {tab.panel}
        </div>
      ))}
    </div>
  );
}
